import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    addToCartMutationFn,
    getCurrentCartQueryFn,
    removeFromCartMutationFn,
} from '@/queries/cart';
import { Cart, CouponUsageCalculationDto, Item } from '@/api';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import {
    BidNotAllowedErrorCode,
    BuyNotAllowedException,
} from '@/helpers/Exceptions';
import toast from 'react-hot-toast';
import i18next from 'i18next';
import { toStripeAmount } from '@/app/utils/stripe';
import { ClickResolveDetails } from '@stripe/stripe-js';
import * as uuid from 'uuid';
import { useLayout } from '@/contexts/LayoutContext';
import { useCallback, useEffect, useRef } from 'react';

let globalCalculateCounter = 0; // tracks if anyone is consuming cart and requires calculations to be executed

type CartWithOptionalShipping<T extends boolean> = T extends true
    ? Cart
    : Omit<Cart, 'shipping_amount' | 'tax_amount' | 'fee_amount' | 'total_amount'>;

const useCart = <T extends boolean>(calculate: T = false as T) => {
    const queryClient = useQueryClient();
    const { showCart, toggleCart } = useLayout();

    const setCartData = useCallback((newCart: Cart) => {
        queryClient.setQueryData(['cart'], newCart);
    }, [queryClient]);

    const { data: cart, refetch: refreshCart } = useQuery({
        queryKey: ['cart'],
        queryFn: () => {
            return getCurrentCartQueryFn(globalCalculateCounter > 0); // neext to access counter directly here
        },
        refetchOnWindowFocus: true
    });

    const cartRef = useRef(cart);

    useEffect(() => {
        cartRef.current = cart;
    }, [cart]);

    useEffect(() => {
        if (calculate) {
            // eslint-disable-next-line no-plusplus
            globalCalculateCounter++;
            if (globalCalculateCounter === 1 && !cartRef.current?.is_calculated) {
                refreshCart(); // refresh with calculate = true
            }
        }

        return () => {
            if (calculate) {
                globalCalculateCounter = Math.max(0, globalCalculateCounter - 1);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculate]);

    const shouldCalculate = globalCalculateCounter > 0;

    const parseException = async (
        exceptionCode: BuyNotAllowedException['code']
    ) => {
        let errorMessage = i18next.t('cart.error-generic') as string;
        switch (exceptionCode) {
            case BidNotAllowedErrorCode.ItemNotForSale:
                errorMessage = i18next.t('cart.error-item-not-for-sale');
                break;
            case BidNotAllowedErrorCode.ItemSoldOut:
                errorMessage = i18next.t('cart.error-item-sold-out');
                break;
            case BidNotAllowedErrorCode.QuantityNotAvailable:
                errorMessage = i18next.t(
                    'cart.error-item-quantity-not-available'
                );
                break;
            default:
                break;
        }

        return errorMessage;
    };

    const addToCartMutation = useMutation({
        mutationFn: addToCartMutationFn,
        onSuccess: (data) => {
            setCartData(data);
            queryClient.invalidateQueries(['getCartMayAlsoLikeQueryFn']);
        },
        onError: async (error: Error) => {
            const exception = await ErrorHandler.transformError(error);
            let errorMessage: string;
            if (exception instanceof BuyNotAllowedException) {
                errorMessage = await parseException(exception.code);
            } else {
                errorMessage = i18next.t('cart.error-generic');
            }
            toast.error(errorMessage);
        },
    });

    const getDiscountType = () => {
        if (
            cart?.coupon_calculation?.type ===
            CouponUsageCalculationDto.TypeEnum.PercentageOff
        ) {
            return `${cart?.coupon_calculation?.value}% off`;
        }
        if (
            cart?.coupon_calculation?.type ===
            CouponUsageCalculationDto.TypeEnum.DollarOff
        ) {
            return `$${cart?.coupon_calculation?.value} off`;
        }
        return '';
    };

    const generateExpressCheckoutOptions = (cartToUse: Cart | undefined = cart) => {
        if (!cartToUse) {
            return;
        }
        const lineItems = cartToUse.items.map((item) => {
            let itemName = item.item.name;
            if (item.quantity > 1) {
                itemName += ` X ${item.quantity}`;
            }
            return {
                amount: toStripeAmount(item.item_amount) * item.quantity,
                name: `${itemName}`,
            };
        });
        lineItems?.push({
            name: i18next.t('estimate.taxes'),
            amount: toStripeAmount(cartToUse?.tax_amount || '0.00'),
        });
        lineItems?.push({
            name: i18next.t('estimate.fees'),
            amount: toStripeAmount(cartToUse?.fee_amount || '0.00'),
        });
        lineItems?.push({
            name: i18next.t('estimate.shipping'),
            amount: toStripeAmount(cartToUse?.shipping_amount || '0.00'),
        });
        if (cartToUse?.coupon_calculation?.off_amount) {
            lineItems?.push({
                name: `${i18next.t('checkout.promo')} - ${cart?.coupon_calculation?.coupon
                    } - ${getDiscountType()}`,
                amount: -toStripeAmount(
                    cartToUse?.coupon_calculation?.off_amount || '0.00'
                ),
            });
        }
        const options: ClickResolveDetails = {
            shippingAddressRequired: true,
            billingAddressRequired: false,
            emailRequired: true,
            phoneNumberRequired: true,
            shippingRates: [
                {
                    amount: toStripeAmount(cartToUse?.shipping_amount),
                    displayName: 'Shipping',
                    id: uuid.v4(),
                },
            ],
            lineItems,
        };
        return options;
    };

    const removeFromCartMutation = useMutation({
        mutationFn: removeFromCartMutationFn,
        onSuccess: (data) => {
            setCartData(data);
            queryClient.invalidateQueries(['getCartMayAlsoLikeQueryFn']);
        },
        onError: async (error: Error) => {
            const exception = await ErrorHandler.transformError(error);
            let errorMessage: string;
            if (exception instanceof BuyNotAllowedException) {
                errorMessage = await parseException(exception.code);
            } else {
                errorMessage = i18next.t('cart.error-generic');
            }
            toast.error(errorMessage);
        },
    });

    const addToCart = async (item: Item) => {
        const existingItem = cart?.items.find(
            (cartItem) => cartItem.item.id === item.id
        );
        let quantity = 1;
        if (existingItem) {
            quantity = existingItem.quantity + 1;
        }
        return addToCartMutation.mutateAsync({
            itemId: item.id,
            quantity: { quantity },
            calculate: shouldCalculate
        }).then(() => {
            if (!showCart) {
                toggleCart();
            }
            return null;
        });
    };

    const updateQuantity = async (
        itemId: string,
        quantity: number,
        pickupOption?: null | string
    ) => {
        return addToCartMutation.mutateAsync({
            itemId,
            quantity: { quantity, pickup_option: pickupOption || undefined },
            calculate: shouldCalculate
        });
    };

    const removeItem = async (itemId: string) => {
        return removeFromCartMutation.mutateAsync({ itemId, calculate: shouldCalculate });
    };

    return {
        addToCart,
        updateQuantity,
        removeItem,
        refreshCart,
        setCartData,
        getDiscountType,
        generateExpressCheckoutOptions,
        cart: cart as CartWithOptionalShipping<T>,
        cartUpdating:
            addToCartMutation.isLoading || removeFromCartMutation.isLoading,
    };
};

export default useCart;
